/** Plans, caps and the 30-day trial. After the trial the account drops to Free; nothing is ever locked. */
import { DAY, toMs } from './time';
import type { Business, IsoUtc, PlanTier } from './types';

export const TRIAL_DAYS = 30;
export type EffectiveTier = Exclude<PlanTier, 'trial'>;

export interface PlanCaps {
  tier: EffectiveTier;
  /** Monthly price including 18% VAT, NIS. */
  priceNis: number;
  /** Automatic WhatsApp messages per month (0 = manual wa.me queue only). */
  messageAllowance: number;
  autoMessaging: boolean;
  maxStaff: number;
  /** null = unlimited. */
  monthlyBookingCap: number | null;
  deposits: boolean;
  waitlist: boolean;
  reports: boolean;
}

export const PLAN_CAPS: Record<EffectiveTier, PlanCaps> = {
  free: { tier: 'free', priceNis: 0, messageAllowance: 0, autoMessaging: false, maxStaff: 1, monthlyBookingCap: 30, deposits: false, waitlist: false, reports: false },
  solo: { tier: 'solo', priceNis: 49, messageAllowance: 400, autoMessaging: true, maxStaff: 1, monthlyBookingCap: null, deposits: true, waitlist: true, reports: true },
  studio: { tier: 'studio', priceNis: 99, messageAllowance: 1200, autoMessaging: true, maxStaff: 5, monthlyBookingCap: null, deposits: true, waitlist: true, reports: true },
  salon: { tier: 'salon', priceNis: 169, messageAllowance: 3000, autoMessaging: true, maxStaff: 15, monthlyBookingCap: null, deposits: true, waitlist: true, reports: true },
};
/** Top-up pack: 500 messages for NIS 25. */
export const TOP_UP = { messages: 500, priceNis: 25 } as const;

export interface ResolvedPlan {
  /** Plan whose caps apply right now (trial counts as Studio, expired trial as Free). */
  tier: EffectiveTier;
  configured: PlanTier;
  inTrial: boolean;
  trialExpired: boolean;
  trialDaysLeft: number;
  trialEndsAt: IsoUtc;
  caps: PlanCaps;
  /** Monthly allowance including purchased top-ups. */
  allowance: number;
}

export function resolvePlan(business: Pick<Business, 'plan' | 'trialStartedAt' | 'topUpMessages'>, nowMs: number): ResolvedPlan {
  const endsMs = toMs(business.trialStartedAt) + TRIAL_DAYS * DAY;
  const trialRunning = business.plan === 'trial' && nowMs < endsMs;
  const trialExpired = business.plan === 'trial' && !trialRunning;
  const tier: EffectiveTier = trialRunning ? 'studio' : business.plan === 'trial' ? 'free' : business.plan;
  const caps = PLAN_CAPS[tier];
  return {
    tier, configured: business.plan, inTrial: trialRunning, trialExpired,
    trialDaysLeft: trialRunning ? Math.ceil((endsMs - nowMs) / DAY) : 0,
    trialEndsAt: new Date(endsMs).toISOString(),
    caps,
    allowance: caps.autoMessaging ? caps.messageAllowance + business.topUpMessages : 0,
  };
}

/** Returns the persisted downgrade (plan -> 'free') when the trial has ended, otherwise undefined. */
export function downgradeIfTrialExpired(business: Business, nowMs: number, nowIso: IsoUtc): Business | undefined {
  return resolvePlan(business, nowMs).trialExpired ? { ...business, plan: 'free', updatedAt: nowIso } : undefined;
}
